import React from 'react';
import i18next from 'i18next';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import moment from 'moment';

import { withTheme } from '@material-ui/core/styles';

import { getFormat, twoDigits, makeGraph, getDate } from 'lib/commonLib';
import { adexDayGraph } from 'lib/api/adex';

import CurrentStatus from 'components/atoms/CurrentStatus';
import OutlinedButton from 'components/atoms/OutlinedButton';
import ContentsWrap from 'components/molecules/ContentsWrap';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import ChartButtonWrap from 'components/molecules/ChartButtonWrap';
import SwitchingArea from 'components/organisms/SwitchingArea';
import MultiplexChart from 'components/organisms/Chart/MultiplexChart';

const graphList = (index, adexIds) => {
  let keyStr;
  const graph = [];

  for (let i = 0; i < adexIds.length; i++) {
    const key = Object.keys(adexIds[i])[0];
    const val = adexIds[i][Object.keys(adexIds[i])[0]];
    const yAxisData = { left: {}, right: {} };
    const customizeData = [];
    switch (index) {
      case 0:
        keyStr = key.toUpperCase() + '_ADVRTS_AMT';
        customizeData[keyStr] = { keyLabel: i18next.t('지출금액') };
        break;
      case 1:
        keyStr = key.toUpperCase() + '_PAR_EPRS_CNT';
        customizeData[keyStr] = { keyLabel: i18next.t('노출') };
        break;
      case 2:
        keyStr = key.toUpperCase() + '_EXL_ITL_CLICK_RATE';
        customizeData[keyStr] = {
          keyLabel: 'CTR',
          tooltipFormat: '0.000',
          isPercent: true,
        };
        yAxisData['left']['isPercent'] = true;
        break;
      case 3:
        keyStr = key.toUpperCase() + '_EXL_ITL_ECPM';
        customizeData[keyStr] = { keyLabel: 'ECPM' };
        break;
      default:
        break;
    }

    yAxisData['left'][keyStr] = true;
    const item = {
      data: { bar: [keyStr] },
      title: val,
      yAxisData: yAxisData,
      customizeData: customizeData,
    };
    graph[i] = item;
  }
  return graph;
};

class AdExPrivateStatisticsGraph extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      graphData: [],
      infoMessage: '',
      loading: false,
      error: false,
    };
  }

  graphButtons = [
    {
      title: i18next.t('지출금액'),
    },
    {
      title: i18next.t('노출'),
    },
    {
      title: i18next.t('CTR'),
    },
    {
      title: i18next.t('ECPM'),
    },
  ];

  getData = () => {
    const { params, locale, t, adexIds } = this.props;
    const sDate = moment().subtract(6, 'month');
    const eDate = moment();
    const format = getFormat(locale);
    this.setState({
      ...this.state,
      loading: true,
      error: false,
      infoMessage: t('까지의 통계 그래프입니다.', {
        date: t('term', {
          startDate: moment(sDate).format(format),
          endDate: moment(eDate).format(format),
        }),
      }),
    });
    adexDayGraph({
      ...params,
      group: 'month',
      sDate: getDate(sDate),
      eDate: getDate(eDate),
    })
      .then((response) => {
        const { data } = response.data;

        this.setState({
          ...this.state,
          primitiveData: data,
          loading: false,
        });

        this.handleChangeGraph(graphList(0, adexIds), 0)();
      })
      .catch((error) => {
        console.log(error);
      });
  };

  componentDidMount() {
    this.getData();
  }

  componentDidUpdate(prevProps) {
    const { params: prevParams } = prevProps;
    const { params } = this.props;

    if (JSON.stringify(prevParams) !== JSON.stringify(params)) {
      this.getData();
    }
  }

  /**
   * 새로고침
   */
  handleRefesh = () => {
    this.getData();
  };

  // 월 일 or 일 반환
  makeLabel = (onlyDay) => (originalDate) => {
    const { t } = this.props;
    const dateObj = moment(originalDate, 'YYYYMMDD');
    const month = twoDigits(dateObj.month() + 1);
    const year = twoDigits(dateObj.year());

    return onlyDay ? (
      `${t(month + '월')}`
    ) : (
      <span>
        {`${t(year + '년')}`}
        {`${t(month + '월')}`}
      </span>
    );
  };

  xAxisTick = (tickProps) => {
    const { x, y, payload } = tickProps;
    const { value } = payload;

    const dayStr = this.makeLabel(true)(value);

    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={-8} dy={16} textAnchor="end" transform="rotate(-55)">
          {dayStr}
        </text>
      </g>
    );
  };

  /**
   * 버튼 클릭하여 그래프 데이터 변경
   */
  handleChangeGraph = (graphDatas, index) => (e) => {
    const { primitiveData } = this.state;
    const { classes } = this.props;
    const graphCompnenetResult = [];
    for (let i = 0; i < graphDatas.length; i++) {
      const {
        title,
        data,
        yAxisData,
        customizeData: customize,
        isPercent = false,
      } = graphDatas[i];
      if (typeof data !== 'undefined') {
        const { graphData, dataKeys, customizeData, keyLabel, stackId } =
          makeGraph({ data, customize, primitiveData });

        const result = (
          <>
            <MultiplexChart
              xAxisTick={this.xAxisTick}
              makeLabel={this.makeLabel(false)}
              className={`${classes.marginR0}`}
              data={graphData}
              dataKeys={dataKeys}
              customizeData={customizeData}
              keyLabel={keyLabel}
              stackId={stackId.length !== 0 ? stackId.join('$') : null}
              yAxisData={yAxisData}
              title={title}
              isPercent={isPercent}
              key={title}
            />
          </>
        );

        graphCompnenetResult.push(result);
      }
    }

    this.setState({
      ...this.state,
      curGraphComponent: graphCompnenetResult,
      selectedIdx: index,
    });
  };

  render() {
    const { handleRefesh, handleChangeGraph } = this;
    const { t, adexIds } = this.props;
    const {
      infoMessage = '애드익스별 6개월 그래프입니다.',
      curGraphComponent,
      selectedIdx,
      primitiveData,
      loading,
      error,
    } = this.state;

    return (
      <SwitchingArea
        title={
          <>
            <InfoTitleArea
              title={t('애드익스별 6개월 그래프')}
              tooltipTitle={infoMessage}
              iconType="fainfoCircle"
              iconColor="graph"
            />
          </>
        }
        handleRefesh={handleRefesh}
      >
        {loading || error ? (
          <CurrentStatus type={loading ? 'loading' : error && 'error'} />
        ) : (
          <>
            <ChartButtonWrap>
              {this.graphButtons.map((item, index) => {
                return (
                  <OutlinedButton
                    key={item.title}
                    onClick={handleChangeGraph(
                      graphList(index, adexIds),
                      index
                    )}
                    selected={selectedIdx === index}
                    disabled={!Boolean(primitiveData)}
                  >
                    {item.title}
                  </OutlinedButton>
                );
              })}
            </ChartButtonWrap>
            <ContentsWrap contentsCount={1}>{curGraphComponent}</ContentsWrap>
          </>
        )}
      </SwitchingArea>
    );
  }
}

export default withTranslation()(
  connect(
    ({ locale }) => ({
      locale: locale.locale,
    }),
    null
  )(withTheme(AdExPrivateStatisticsGraph))
);
